import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import * as d3 from "d3";
import {sizeCircleRadius} from "../../mocks/projectsMock";
import renderLogoImage from "./components/LogoImage";
import useFetchDataLogotypeGpb from "../../hooks/useFetchDataLogotypeGpb";
import {ITypeRadar} from "../../types";
import {SidebarTab} from "../Sidebar";
import {IOrganizationSegment} from "../../hooks/useFetchDataCrgciAuthorities";

interface IProps {
  openSidebarWithTab?: (
    tab: SidebarTab,
    directionId: number | string,
    trendId?: number | string,
    trendTitle?: string,
    sectionName?: string,
    segmentColor?: {back: string; color: string},
    nameTehnology?: string,
    idTehnology?: number | string,
    typeRadar?: any,
    isSearchPanelActive?: boolean
  ) => void;
  onResetActiveSegmentReady?: (resetFn: () => void) => void;
  selectedWorkerData?: any;
  authoritiesRadarData?: any[];
  radarSectors?: Array<{
    name: string;
    color: string;
    angle: number;
  }>;
  segmentsData?: IOrganizationSegment[];
}

export const RadarChartStateOrganisations: React.FC<IProps> = ({
  openSidebarWithTab,
  onResetActiveSegmentReady,
  selectedWorkerData,
  authoritiesRadarData = [],
  radarSectors = [],
  segmentsData = [],
}) => {
  const {settingData} = useFetchDataLogotypeGpb();

  const isShowLogotypeGpb = settingData?.data?.showGpb || false;
  const svgRef = useRef<SVGSVGElement | null>(null);
  const typeRadar: ITypeRadar = "stateInitiative";
  const [hoveredPoint, setHoveredPoint] = useState<string | null>(null);
  const [clickedPoint, setClickedPoint] = useState<string | null>(null);

  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const center = useMemo(
    () => ({x: dimensions.width / 2, y: dimensions.height / 2}),
    [dimensions]
  );

  const {logoRadius} = sizeCircleRadius;

  const outerCircleRadius = useMemo(() => {
    return Math.min(dimensions.height * 0.35, dimensions.width * 0.35);
  }, [dimensions]);

  const innerCircleRadius = useMemo(() => {
    return outerCircleRadius * 0.2;
  }, [outerCircleRadius]);

  const scaleFactor = useMemo(() => {
    return outerCircleRadius / 300;
  }, [outerCircleRadius]);

  const organizationsData = useMemo(() => {
    if (segmentsData && segmentsData.length > 0) {
      const dataObject: {[key: string]: any} = {};

      segmentsData.forEach((segment, sectorIndex) => {
        segment.organizations.forEach((org, pointIndex) => {
          const pointId = `sector-${sectorIndex}-point-${pointIndex}`;

          const finalInitiativesCount = org.initiatives?.length || 0;

          dataObject[pointId] = {
            id: org.id,
            documentId: org.documentId,
            name: org.name,
            fullName: org.name,
            description: "",
            initiatives: org.initiatives || [],
            initiativesCount: finalInitiativesCount,
            type: "Организация",
            sector: segment.name,
            x: 0,
            y: 0,
            sectorName: segment.name,
            sectorColor: "#3290B7",
          };
        });
      });

      return dataObject;
    }

    if (authoritiesRadarData.length > 0) {
      const dataObject: {[key: string]: any} = {};

      authoritiesRadarData.forEach((org: any, index: number) => {
        const pointId = `org-${org.id}`;

        const finalInitiativesCount = org.initiatives?.length || 0;

        dataObject[pointId] = {
          id: org.id,
          name: org.name,
          fullName: org.fullName || org.name,
          description: org.description || "",
          initiatives: org.initiatives || [],
          initiativesCount: finalInitiativesCount,
          type: org.type || "Организация",
          sector: org.sector || "Органы власти",
          x: org.x * outerCircleRadius,
          y: org.y * outerCircleRadius,
          sectorName: org.sectorName,
          sectorColor: org.sectorColor,
        };
      });

      return dataObject;
    }

    return {};
  }, [segmentsData, authoritiesRadarData, outerCircleRadius]);

  const handleSecondRowClick = useCallback(
    (pointId: string) => {
      if (
        openSidebarWithTab &&
        organizationsData[pointId as keyof typeof organizationsData]
      ) {
        const orgData =
          organizationsData[pointId as keyof typeof organizationsData];

        openSidebarWithTab(
          SidebarTab.StateOrganisations,
          orgData.id,
          orgData.id,
          orgData.name,
          orgData.sector,
          {back: "#3290B7", color: "#11A0DA"}
        );
      }
    },
    [openSidebarWithTab, organizationsData]
  );

  const sectorData = useMemo(() => {
    if (segmentsData && segmentsData.length > 0) {
      const anglePerSector = 360 / segmentsData.length;

      return segmentsData.map((segment, index) => {
        const startAngle = index * anglePerSector - 90;
        const endAngle = (index + 1) * anglePerSector - 90;

        return {
          startAngle,
          endAngle,
          pointCount: segment.organizations.length,
          label: segment.name,
          segmentData: segment,
        };
      });
    }

    return [
      {
        startAngle: -90,
        endAngle: 0,
        pointCount: 5,
        label: "Органы власти",
      },
      {
        startAngle: 0,
        endAngle: 90,
        pointCount: 4,
        label: "Рынок",
      },
      {
        startAngle: 90,
        endAngle: 180,
        pointCount: 4,
        label: "Ассоциации и площадки",
      },
      {
        startAngle: 180,
        endAngle: 270,
        pointCount: 4,
        label: "Банк России",
      },
    ];
  }, [segmentsData]);

  const createSectorPoints = useCallback(
    (startAngle: number, endAngle: number, pointCount: number) => {
      const middleRadius = (outerCircleRadius + innerCircleRadius) / 2;
      const sectorAngle = endAngle - startAngle;
      const points = [];

      if (pointCount > 0) {
        const subSectorAngle = sectorAngle / pointCount;

        for (let i = 0; i < pointCount; i++) {
          const subSectorCenter = startAngle + (i + 0.5) * subSectorAngle;
          const angleRad = (subSectorCenter * Math.PI) / 180;

          points.push({
            x: middleRadius * Math.cos(angleRad),
            y: middleRadius * Math.sin(angleRad),
          });
        }
      }

      return points;
    },
    [outerCircleRadius, innerCircleRadius]
  );

  const createLabelPosition = useCallback(
    (pointX: number, pointY: number) => {
      const baseLabelOffset = 30 * scaleFactor;
      const angle = Math.atan2(pointY, pointX);
      const angleDeg = ((angle * 180) / Math.PI + 360) % 360;
      let labelOffset = baseLabelOffset;
      const distanceFromCenter = Math.sqrt(pointX * pointX + pointY * pointY);
      const relativeDistance = distanceFromCenter / outerCircleRadius;

      if (relativeDistance > 0.8) {
        labelOffset = baseLabelOffset * 1.5;
      } else if (relativeDistance < 0.4) {
        labelOffset = baseLabelOffset * 0.7;
      }
      const labelX = pointX + labelOffset * Math.cos(angle);
      const labelY = pointY + labelOffset * Math.sin(angle);

      const isRightHalf = pointX > 0;

      return {
        x: labelX,
        y: labelY,
        isRightHalf,
      };
    },
    [outerCircleRadius, scaleFactor]
  );

  useEffect(() => {
    const handleResize = () => {
      setDimensions({width: window.innerWidth, height: window.innerHeight});
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const initializeSvg = useCallback(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const mainGroup = svg
      .append("g")
      .attr("transform", `translate(${center.x},${center.y})`);

    const sectorsGroup = mainGroup.append("g").attr("class", "sectors");
    const labelsGroup = mainGroup.append("g").attr("class", "labels");
    const pointsGroup = mainGroup.append("g").attr("class", "points");
    const compactLabelsGroup = mainGroup
      .append("g")
      .attr("class", "compact-labels");
    const expandedLabelsGroup = mainGroup
      .append("g")
      .attr("class", "expanded-labels");
    const logoGroup = mainGroup.append("g").attr("class", "logo");

    const labelRadius = outerCircleRadius + 15 * scaleFactor;

    const defs = svg.append("defs");

    const segmentLabels = sectorData.map((sector) => sector.label);

    for (let i = 0; i < sectorData.length; i++) {
      const sector = sectorData[i];
      const startAngle = sector.startAngle;
      const endAngle = sector.endAngle;

      const startAngleRad = (startAngle * Math.PI) / 180;
      const endAngleRad = (endAngle * Math.PI) / 180;

      const midAngle = (startAngle + endAngle) / 2;
      const normalizedAngle = ((midAngle % 360) + 360) % 360;
      const shouldFlipText = normalizedAngle > 90 && normalizedAngle < 180;
      const currentLabelRadius = shouldFlipText
        ? labelRadius + 8 * scaleFactor
        : labelRadius;

      const x1 = currentLabelRadius * Math.cos(startAngleRad);
      const y1 = currentLabelRadius * Math.sin(startAngleRad);
      const x2 = currentLabelRadius * Math.cos(endAngleRad);
      const y2 = currentLabelRadius * Math.sin(endAngleRad);

      if (shouldFlipText) {
        defs
          .append("path")
          .attr("id", `arc-segment-${i}`)
          .attr(
            "d",
            `M ${x2} ${y2} A ${currentLabelRadius} ${currentLabelRadius} 0 0 0 ${x1} ${y1}`
          );
      } else {
        defs
          .append("path")
          .attr("id", `arc-segment-${i}`)
          .attr(
            "d",
            `M ${x1} ${y1} A ${currentLabelRadius} ${currentLabelRadius} 0 0 1 ${x2} ${y2}`
          );
      }
    }

    const segmentCount = sectorData.length;
    const segmentAngle = 360 / segmentCount;

    for (let i = 0; i < segmentCount; i++) {
      const startAngle = i * segmentAngle - 90;
      const endAngle = (i + 1) * segmentAngle - 90;

      const isActiveSector =
        clickedPoint && clickedPoint.startsWith(`sector-${i}-`);

      const startAngleRad = (startAngle * Math.PI) / 180;
      const endAngleRad = (endAngle * Math.PI) / 180;

      const x1Outer = outerCircleRadius * Math.cos(startAngleRad);
      const y1Outer = outerCircleRadius * Math.sin(startAngleRad);
      const x2Outer = outerCircleRadius * Math.cos(endAngleRad);
      const y2Outer = outerCircleRadius * Math.sin(endAngleRad);

      const x1Inner = innerCircleRadius * Math.cos(startAngleRad);
      const y1Inner = innerCircleRadius * Math.sin(startAngleRad);
      const x2Inner = innerCircleRadius * Math.cos(endAngleRad);
      const y2Inner = innerCircleRadius * Math.sin(endAngleRad);

      const pathData = [
        `M ${x1Outer} ${y1Outer}`,
        `A ${outerCircleRadius} ${outerCircleRadius} 0 0 1 ${x2Outer} ${y2Outer}`,
        `L ${x2Inner} ${y2Inner}`,
        `A ${innerCircleRadius} ${innerCircleRadius} 0 0 0 ${x1Inner} ${y1Inner}`,
        `Z`,
      ].join(" ");

      sectorsGroup
        .append("path")
        .attr("d", pathData)
        .style(
          "fill",
          isActiveSector
            ? "rgba(17, 160, 218, 0.33)"
            : "rgba(17, 160, 218, 0.1)"
        )
        .style(
          "stroke",
          isActiveSector ? "rgba(17, 160, 218, 0.8)" : "rgba(17, 160, 218, 0.5)"
        )
        .style("stroke-width", isActiveSector ? 2 : 1);
    }

    segmentLabels.forEach((label, index) => {
      const isMarketSector = label === "Рынок";

      const textElement = labelsGroup
        .append("text")
        .attr("font-size", 18 * scaleFactor)
        .attr("fill", "rgba(17, 160, 218, 0.83)")
        .attr("text-anchor", "middle");

      if (isMarketSector) {
        const sector = sectorData[index];
        const midAngle = (sector.startAngle + sector.endAngle) / 2;
        const marketLabelRadius = outerCircleRadius + 20 * scaleFactor;
        const midAngleRad = (midAngle * Math.PI) / 180;
        const centerX = marketLabelRadius * Math.cos(midAngleRad);
        const centerY = marketLabelRadius * Math.sin(midAngleRad);

        textElement.attr(
          "transform",
          `translate(${centerX}, ${centerY}) rotate(180) translate(${-centerX}, ${-centerY})`
        );
      }

      textElement
        .append("textPath")
        .attr("href", `#arc-segment-${index}`)
        .attr("startOffset", "50%")
        .text(label);
    });

    if (authoritiesRadarData.length > 0) {
      Object.entries(organizationsData).forEach(([pointId, orgData]) => {
        if (!orgData) return;

        // Используем координаты из переданных данных
        const point = {
          x: orgData.x, // Уже умножено на outerCircleRadius
          y: orgData.y,
        };

        const isHovered = hoveredPoint === pointId;
        const isClicked = clickedPoint === pointId;
        const shouldShowExpanded = isHovered || isClicked;

        const orgName = orgData.name || "Организация";
        const orgInitiatives = orgData.initiativesCount || 0;

        // Точка
        pointsGroup
          .append("circle")
          .attr("cx", point.x)
          .attr("cy", point.y)
          .attr("r", 6 * scaleFactor)
          .style("fill", shouldShowExpanded ? "white" : "#86D1F2")
          .style("stroke", "white")
          .style("stroke-width", 2 * scaleFactor)
          .style("cursor", "pointer")
          .style("transition", "all 0.3s ease")
          .style(
            "filter",
            shouldShowExpanded
              ? `drop-shadow(0 0 ${10 * scaleFactor}px white)`
              : "none"
          )
          .on("mouseenter", () => setHoveredPoint(pointId))
          .on("mouseleave", () => {
            if (!isClicked) setHoveredPoint(null);
          })
          .on("click", () => {
            if (isClicked) {
              setClickedPoint(null);
              setHoveredPoint(null);
            } else {
              setClickedPoint(pointId);
              setHoveredPoint(pointId);
            }
          });

        const labelPos = createLabelPosition(point.x, point.y);

        // Компактный лейбл (когда не развернут)
        if (!shouldShowExpanded) {
          const maxLabelWidth = 120 * scaleFactor;
          const textHeight = 18 * scaleFactor;
          const padding = 6 * scaleFactor;

          const truncateText = (
            text: string,
            maxWidth: number,
            charWidth: number
          ) => {
            const maxChars = Math.floor(maxWidth / charWidth);
            if (text.length <= maxChars) return text;
            const ellipsisChars = 3;
            const truncatedLength = Math.max(1, maxChars - ellipsisChars);
            return text.substring(0, truncatedLength) + "...";
          };

          const charWidth = 5 * scaleFactor;
          const displayText = truncateText(
            orgName,
            maxLabelWidth - padding * 8,
            charWidth
          );
          const textWidth = displayText.length * charWidth;
          const rectWidth = textWidth + padding * 4;
          const rectHeight = textHeight + 3;

          const rectX = labelPos.isRightHalf
            ? labelPos.x
            : labelPos.x - rectWidth;
          const rectY = labelPos.y - rectHeight / 2;

          compactLabelsGroup
            .append("rect")
            .attr("x", rectX)
            .attr("y", rectY)
            .attr("width", rectWidth)
            .attr("height", rectHeight)
            .attr("rx", 6 * scaleFactor)
            .attr("ry", 6 * scaleFactor)
            .style("fill", "#2A4A63")
            .style("stroke", "rgba(255, 255, 255, 0.2)")
            .style("stroke-width", 1 * scaleFactor);

          compactLabelsGroup
            .append("text")
            .attr("x", rectX + rectWidth / 2)
            .attr("y", rectY + rectHeight / 2 + 4.5 * scaleFactor)
            .attr("font-size", 10.5 * scaleFactor)
            .attr("fill", "white")
            .attr("text-anchor", "middle")
            .text(displayText);
        }

        // Развернутый лейбл (когда наводим или кликаем)
        if (shouldShowExpanded) {
          const expandedWidth = 150 * scaleFactor;
          const baseExpandedHeight = 60 * scaleFactor;
          const padding = 6 * scaleFactor;

          const wrapText = (
            text: string,
            maxWidth: number,
            fontSize: number
          ) => {
            const words = text.split(" ");
            const lines = [];
            let currentLine = "";
            const charWidth = fontSize * 0.6;
            for (const word of words) {
              const testLine = currentLine ? `${currentLine} ${word}` : word;
              const testWidth = testLine.length * charWidth;

              if (testWidth <= maxWidth) {
                currentLine = testLine;
              } else {
                if (currentLine) {
                  lines.push(currentLine);
                  currentLine = word;
                } else {
                  lines.push(word);
                  currentLine = "";
                }
              }
            }
            if (currentLine) lines.push(currentLine);
            return lines;
          };

          const closeButtonWidth = 18 * scaleFactor;
          const availableTextWidth =
            expandedWidth - padding * 2 - closeButtonWidth;
          const fontSize = 12 * scaleFactor;
          const textLines = wrapText(orgName, availableTextWidth, fontSize);
          const lineHeight = 14 * scaleFactor;

          const titleHeight = textLines.length * lineHeight;
          const minSpaceBetweenRows = 8 * scaleFactor;
          const titleBottomY = padding + 9 * scaleFactor + titleHeight;
          const secondRowHeight = 12 * scaleFactor;
          const dynamicBaselineYFromTop = Math.max(
            titleBottomY + minSpaceBetweenRows,
            baseExpandedHeight - padding - 9 * scaleFactor
          );

          const minRequiredHeight =
            dynamicBaselineYFromTop + secondRowHeight + padding;
          const expandedHeight = Math.max(
            baseExpandedHeight,
            minRequiredHeight
          );

          const rectX = labelPos.isRightHalf
            ? labelPos.x
            : labelPos.x - expandedWidth;
          const rectY = labelPos.y - expandedHeight / 2;

          const backgroundColor = isClicked ? "#3290B7" : "#0E3747";
          const borderColor = isClicked
            ? "#11A0DA"
            : "rgba(255, 255, 255, 0.2)";

          // Фон лейбла
          expandedLabelsGroup
            .append("rect")
            .attr("x", rectX)
            .attr("y", rectY)
            .attr("width", expandedWidth)
            .attr("height", expandedHeight)
            .attr("rx", 7.5 * scaleFactor)
            .attr("ry", 7.5 * scaleFactor)
            .style("fill", backgroundColor)
            .style("stroke", borderColor)
            .style("stroke-width", 1.5 * scaleFactor);

          // Текст названия
          textLines.forEach((line: string, index: number) => {
            expandedLabelsGroup
              .append("text")
              .attr("x", rectX + padding)
              .attr("y", rectY + padding + 9 * scaleFactor + index * lineHeight)
              .attr("font-size", fontSize)
              .attr("fill", "white")
              .attr("text-anchor", "start")
              .text(line);
          });

          // Кнопка закрытия
          const closeButtonX =
            rectX + expandedWidth - padding - 6 * scaleFactor;
          const closeButtonY = rectY + padding + 6 * scaleFactor;

          expandedLabelsGroup
            .append("circle")
            .attr("cx", closeButtonX)
            .attr("cy", closeButtonY)
            .attr("r", 9 * scaleFactor)
            .style("fill", "rgba(250, 250, 250, 0.16)")
            .style("cursor", "pointer")
            .on("click", (event) => {
              event.stopPropagation();
              setClickedPoint(null);
              setHoveredPoint(null);
            });

          expandedLabelsGroup
            .append("text")
            .attr("x", closeButtonX)
            .attr("y", closeButtonY)
            .attr("dy", "0.35em")
            .attr("font-size", 13.5 * scaleFactor)
            .attr("fill", "white")
            .attr("text-anchor", "middle")
            .style("cursor", "pointer")
            .text("×")
            .on("click", (event) => {
              event.stopPropagation();
              setClickedPoint(null);
              setHoveredPoint(null);
            });

          // Строка с инициативами
          const dynamicBaselineY = rectY + titleBottomY + minSpaceBetweenRows;

          const secondRowGroup = expandedLabelsGroup
            .append("g")
            .style("cursor", "pointer")
            .on("click", () => {
              handleSecondRowClick(pointId);
            });

          const textElement = secondRowGroup
            .append("text")
            .attr("x", rectX + padding)
            .attr("y", dynamicBaselineY)
            .attr("dy", "0.35em")
            .attr("font-size", 9 * scaleFactor)
            .attr("fill", "white")
            .attr("text-anchor", "start")
            .text("Инициативы");

          const textWidth = textElement.node()?.getBBox().width || 0;
          const gapFromText = 8 * scaleFactor;

          const badgeWidth = 24 * scaleFactor;
          const badgeHeight = 12 * scaleFactor;
          const badgeX = rectX + padding + textWidth + gapFromText;
          const badgeY = dynamicBaselineY - badgeHeight / 2;

          secondRowGroup
            .append("rect")
            .attr("x", badgeX)
            .attr("y", badgeY)
            .attr("width", badgeWidth)
            .attr("height", badgeHeight)
            .attr("rx", badgeHeight / 2)
            .attr("ry", badgeHeight / 2)
            .style("fill", "#56B9F2")
            .style("stroke", "rgba(255, 255, 255, 0.2)")
            .style("stroke-width", 1 * scaleFactor);

          secondRowGroup
            .append("text")
            .attr("x", badgeX + badgeWidth / 2)
            .attr("y", badgeY + badgeHeight / 2)
            .attr("dy", "0.35em")
            .attr("font-size", 7 * scaleFactor)
            .attr("fill", "white")
            .attr("text-anchor", "middle")
            .text(orgInitiatives.toString());

          const arrowX = rectX + expandedWidth - padding - 6 * scaleFactor;

          secondRowGroup
            .append("text")
            .attr("x", arrowX)
            .attr("y", dynamicBaselineY)
            .attr("dy", "0.35em")
            .attr("font-size", 24 * scaleFactor)
            .attr("fill", "white")
            .attr("fill-opacity", 0.5)
            .attr("text-anchor", "middle")
            .text("›");
        }
      });
    }

    renderLogoImage(
      logoGroup,
      isShowLogotypeGpb,
      logoRadius.radius * scaleFactor,
      typeRadar
    );
  }, [
    center,
    isShowLogotypeGpb,
    logoRadius.radius,
    typeRadar,
    hoveredPoint,
    clickedPoint,
    handleSecondRowClick,
    sectorData,
    createSectorPoints,
    createLabelPosition,
    outerCircleRadius,
    innerCircleRadius,
    scaleFactor,
    organizationsData,
  ]);

  useEffect(() => {
    initializeSvg();
  }, [initializeSvg]);

  useEffect(() => {
    if (onResetActiveSegmentReady) {
      const resetFn = () => {
        setClickedPoint(null);
        setHoveredPoint(null);
      };
      onResetActiveSegmentReady(resetFn);
    }
  }, [onResetActiveSegmentReady]);

  return (
    <div>
      <svg ref={svgRef} width={dimensions.width} height={dimensions.height} />
      {selectedWorkerData && (
        <div
          style={{
            position: "absolute",
            bottom: "100px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "rgba(42, 74, 191, 0.9)",
            color: "white",
            padding: "16px 24px",
            borderRadius: "12px",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            textAlign: "center",
            minWidth: "300px",
            backdropFilter: "blur(10px)",
          }}
        >
          <div
            style={{fontSize: "18px", fontWeight: "600", marginBottom: "8px"}}
          >
            {selectedWorkerData.fullName}
          </div>
          <div style={{fontSize: "14px", opacity: 0.8, marginBottom: "4px"}}>
            {selectedWorkerData.department}
          </div>
          <div style={{fontSize: "14px", opacity: 0.8}}>
            {selectedWorkerData.role}
          </div>
        </div>
      )}
    </div>
  );
};
